// ══════════════════════════════════════════════════════════════════════════════
// PriestAltarPage.js — Second device shrine/altar camera
// Access: /?altar=<booking_id>&secret=<admin_secret>
// Open this on a phone or tablet placed facing the deity and puja items.
// Publishes video-only (no audio to prevent echo from same room).
// ══════════════════════════════════════════════════════════════════════════════
import { useState, useEffect } from "react";
import AgoraRTC, {
  AgoraRTCProvider,
  LocalVideoTrack,
  useJoin,
  useLocalCameraTrack,
  usePublish,
} from "agora-rtc-react";

const client = AgoraRTC.createClient({ mode: "rtc", codec: "vp8" });
const API    = process.env.REACT_APP_API_URL || "http://localhost:8000";

const C = {
  bg: "#06030A", bg2: "#0D0818",
  saffron: "#E8832A", gold: "#C9953A",
  cream: "#FFF5E0", dim: "#8A7558",
  border: "rgba(201,149,58,0.25)",
};

function AltarInner({ config, onStop }) {
  const [elapsed, setElapsed] = useState(0);
  const [joined,  setJoined]  = useState(false);

  useEffect(() => {
    const t = setInterval(() => setElapsed(s => s + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const fmtTime = s =>
    `${String(Math.floor(s / 60)).padStart(2,"0")}:${String(s % 60).padStart(2,"0")}`;

  // Video only — no mic track to prevent echo from same room
  const { localCameraTrack } = useLocalCameraTrack(true, {
    encoderConfig: {
      width: { ideal: 1280 }, height: { ideal: 720 },
      frameRate: 24, bitrateMin: 400, bitrateMax: 1200,
      facingMode: "environment", // use rear/back camera on mobile
    },
  });
  usePublish([localCameraTrack]);

  const { isLoading } = useJoin({ appid: config.appId, channel: config.channel, token: config.token, uid: config.uid }, true);

  useEffect(() => { if (!isLoading) setJoined(true); }, [isLoading]);

  useEffect(() => {
    return () => { localCameraTrack?.stop(); localCameraTrack?.close(); };
  }, [localCameraTrack]);

  const handleStop = () => {
    localCameraTrack?.stop(); localCameraTrack?.close();
    onStop();
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "#000", zIndex: 1000 }}>
      {/* Full-screen camera preview */}
      {localCameraTrack && (
        <LocalVideoTrack
          track={localCameraTrack}
          play
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
        />
      )}

      {/* Top status overlay */}
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0,
        padding: "16px 20px",
        background: "linear-gradient(rgba(6,3,10,0.85), transparent)",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <span style={{ fontSize: "1.4rem", filter: "drop-shadow(0 0 8px rgba(232,131,42,0.6))" }}>🔱</span>
          <div>
            <div style={{ fontFamily: "'Cinzel Decorative', serif", fontSize: "0.82rem", color: C.saffron }}>
              {config.pujaName}
            </div>
            <div style={{ fontSize: "0.68rem", color: C.dim, marginTop: "1px" }}>
              Shrine Cam · Rear Camera
            </div>
          </div>
        </div>
        <div style={{
          display: "flex", alignItems: "center", gap: "6px",
          background: "rgba(34,197,94,0.15)", border: "1px solid rgba(34,197,94,0.3)",
          borderRadius: "20px", padding: "4px 12px",
          fontSize: "0.7rem", color: "#86efac",
        }}>
          <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: "#22c55e", display: "inline-block", boxShadow: "0 0 6px #22c55e" }} />
          {joined ? `LIVE · ${fmtTime(elapsed)}` : "Connecting…"}
        </div>
      </div>

      {/* Framing guide — center box to help align deity */}
      <div style={{
        position: "absolute", top: "50%", left: "50%",
        transform: "translate(-50%, -50%)",
        width: "60%", height: "55%",
        border: "1px dashed rgba(232,131,42,0.3)",
        borderRadius: "8px", pointerEvents: "none",
      }} />
      <div style={{
        position: "absolute", top: "50%", left: "50%",
        transform: "translate(-50%, calc(-50% - 20px))",
        fontSize: "0.65rem", color: "rgba(232,131,42,0.45)",
        fontFamily: "'Cinzel Decorative', serif", letterSpacing: "0.06em",
        pointerEvents: "none", whiteSpace: "nowrap",
      }}>
        Frame deity here
      </div>

      {/* Stop button */}
      <div style={{
        position: "absolute", bottom: 0, left: 0, right: 0,
        padding: "20px",
        background: "linear-gradient(transparent, rgba(6,3,10,0.85))",
        display: "flex", justifyContent: "center",
      }}>
        <button
          onClick={handleStop}
          style={{
            background: "rgba(239,68,68,0.2)", border: "1px solid rgba(239,68,68,0.4)",
            borderRadius: "12px", padding: "12px 36px",
            color: "#fca5a5", fontFamily: "'Crimson Pro', serif",
            fontSize: "1rem", cursor: "pointer",
          }}
        >
          Stop Shrine Cam
        </button>
      </div>
    </div>
  );
}

export default function PriestAltarPage({ bookingId, secret }) {
  const [state,  setState]  = useState("loading"); // loading | ready | live | stopped | error
  const [config, setConfig] = useState(null);
  const [errMsg, setErrMsg] = useState("");

  useEffect(() => {
    if (!bookingId || !secret) { setState("error"); setErrMsg("Missing booking ID or secret."); return; }
    fetch(`${API}/puja/altar-token/${bookingId}?secret=${encodeURIComponent(secret)}`)
      .then(r => r.json())
      .then(data => {
        if (data.detail) throw new Error(data.detail);
        setConfig({ appId: data.app_id, token: data.token, channel: data.channel, uid: data.uid, pujaName: data.puja_name });
        setState("ready");
      })
      .catch(e => { setState("error"); setErrMsg(e.message || "Could not load session."); });
  }, [bookingId, secret]);

  if (state === "loading") return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: "3rem", color: C.saffron, marginBottom: "12px" }}>ॐ</div>
        <div style={{ color: C.dim, fontSize: "0.85rem", fontStyle: "italic" }}>Preparing shrine cam…</div>
      </div>
    </div>
  );

  if (state === "error") return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: "24px" }}>
      <div style={{
        background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)",
        borderRadius: "12px", padding: "24px", maxWidth: "360px", textAlign: "center",
        fontSize: "0.9rem", color: "#fca5a5",
      }}>{errMsg}</div>
    </div>
  );

  if (state === "stopped") return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: "3rem", marginBottom: "12px" }}>🙏</div>
        <div style={{ fontFamily: "'Cinzel Decorative', serif", color: C.saffron, fontSize: "1rem" }}>
          Shrine cam stopped
        </div>
      </div>
    </div>
  );

  if (state === "ready") return (
    <div style={{
      minHeight: "100vh", background: C.bg,
      display: "flex", alignItems: "center", justifyContent: "center", padding: "24px",
    }}>
      <div style={{ maxWidth: "380px", width: "100%", textAlign: "center" }}>
        <div style={{ fontSize: "3rem", marginBottom: "16px", filter: "drop-shadow(0 0 16px rgba(232,131,42,0.5))" }}>🔱</div>
        <div style={{ fontFamily: "'Cinzel Decorative', serif", fontSize: "1.1rem", color: C.saffron, marginBottom: "8px" }}>
          Shrine Cam
        </div>
        <div style={{ fontSize: "0.88rem", color: C.dim, lineHeight: "1.7", marginBottom: "8px" }}>
          {config?.pujaName}
        </div>
        <div style={{
          background: C.bg2, border: `1px solid ${C.border}`,
          borderRadius: "12px", padding: "16px 20px", marginBottom: "24px",
          fontSize: "0.8rem", color: C.dim, textAlign: "left", lineHeight: "1.8",
        }}>
          <div style={{ color: C.saffron, fontFamily: "'Cinzel Decorative', serif", fontSize: "0.68rem", marginBottom: "8px", letterSpacing: "0.05em" }}>
            SETUP GUIDE
          </div>
          ✓ Place this device facing the deity idol<br />
          ✓ Keep puja items (lamp, flowers, incense) in frame<br />
          ✓ Ensure good lighting on the altar<br />
          ✓ Keep device steady — prop it against something<br />
          ✓ Keep volume low — mic is off (no echo)
        </div>
        <button
          onClick={() => setState("live")}
          style={{
            width: "100%", padding: "14px",
            background: "linear-gradient(135deg, #E8832A, #C9953A)",
            border: "none", borderRadius: "12px", color: "white",
            fontFamily: "'Cinzel Decorative', serif", fontSize: "0.9rem",
            cursor: "pointer", letterSpacing: "0.04em",
            boxShadow: "0 6px 24px rgba(232,131,42,0.3)",
          }}
        >
          Start Shrine Cam
        </button>
      </div>
    </div>
  );

  return (
    <AgoraRTCProvider client={client}>
      <AltarInner config={config} onStop={() => setState("stopped")} />
    </AgoraRTCProvider>
  );
}
